import React, { FC } from 'react'
import styles from './ExchangeRateInput.module.css'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import classNames from 'classnames'
import ChangeArrowsIcon from '@/icons/change-arrows'
import AmountAdornment from '../amount-adornment'
import Input from '../input'
import { NETWORK } from '@/utils/constants'
import { formatSatsPer } from '@/utils/format-sats'
import { formatUsdPer } from '@/utils/format-usd'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const BTC = NETWORK
const USD = 'USD'

const ExchangeRateInput: FC<{
	amount: string
	setAmount: (value: any) => void
	setBitcoinAmount: (value: any) => void
	className?: string
	inputClassName?: string
	currency?: string
	setCurrency?: (value: any) => void
	floor?: string
	placeholder?: string
}> = (props) => {
	const { amount, setAmount, setBitcoinAmount, className, inputClassName, floor, placeholder } =
		props

	const [inputCurrency, setInputCurrency] = React.useState(BTC)
	const { exchangeRate, isLoading: loadingRate } = useExchangeRate()
	const currency = props.currency || inputCurrency

	const { sats, per } = React.useMemo(() => formatSatsPer(parseFloat(floor as any)), [floor])
	const { sats: _usd } = React.useMemo(
		() => formatSatsPer((parseFloat(floor as any) / 1e8) * exchangeRate),
		[floor, exchangeRate]
	)

	const { bitcoinAmount, usdAmount } = React.useMemo(() => {
		let usdAmount = amount
		let bitcoinAmount = amount

		const tempAmount = amount?.length > 0 ? parseFloat(amount) : 0

		bitcoinAmount = (tempAmount / exchangeRate).toFixed(8).toString()
		usdAmount = (tempAmount * exchangeRate).toFixed(2).toString()

		if (per === 'sats') {
			bitcoinAmount = (tempAmount / 1e8 / exchangeRate).toFixed(8).toString()
			usdAmount = ((tempAmount / 1e8) * exchangeRate).toFixed(2).toString()
		}

		return { bitcoinAmount, usdAmount }
	}, [amount, exchangeRate, per])

	const handleChangeCurrency = () => {
		if (currency === USD) {
			setAmount(amount?.length > 0 ? bitcoinAmount : '')
			props.setCurrency ? props.setCurrency(BTC) : setInputCurrency(BTC)
		} else if (currency === BTC) {
			setAmount(amount?.length > 0 ? usdAmount : '')
			props.setCurrency ? props.setCurrency(USD) : setInputCurrency(USD)
		}
	}

	const handleSetAmount = (e: any) => {
		const value = e.target.value
		setAmount(value)

		if (currency === USD) {
			setBitcoinAmount((parseFloat(value) / exchangeRate) * 1e8)
		} else {
			setBitcoinAmount(parseFloat(value) * 1e8)
		}
	}

	const handleSetFloor = () => {
		// @ts-ignore
		const value = parseFloat(sats)

		if (per === 'sats') {
			if (currency === USD) {
				setAmount((value / 1e8) * exchangeRate)
			} else {
				setAmount(value)
			}
			setBitcoinAmount(value / 1e8)
		} else {
			if (currency === USD) {
				setAmount((value * exchangeRate).toFixed(2))
			} else {
				setAmount(value.toFixed(2))
			}
			setBitcoinAmount(value)
		}
	}

	const error = React.useMemo(() => {
		if (amount?.length > 0 && isNaN(Number(amount))) {
			return true
		}

		return false
	}, [amount])

	return (
		<div
			className={classNames(
				{ [props.className as any]: !!className },
				'flex flex-col gap-2 rounded-full'
			)}
		>
			<Input
				className={classNames(
					{ [props.inputClassName as any]: !!inputClassName },
					'text-base border border-border rounded-full h-10',
					{
						[styles.error]: error
					}
				)}
				inputClassName="rounded-l-full text-sm"
				placeholder={placeholder || 'Amount'}
				value={amount}
				endAdornmentClassName="rounded-r-full select-none cursor-pointer text-muted-foreground flex items-center"
				endAdornment={<AmountAdornment type={per === 'sats' && floor ? 'SATS' : currency} />}
				onEndAdornmentClick={handleChangeCurrency}
				onChange={handleSetAmount}
			/>
			<div className="flex justify-between items-center">
				{!loadingRate && (
					<div
						className="flex select-none cursor-pointer items-center text-xs"
						onClick={handleChangeCurrency}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(handleChangeCurrency)}
					>
						<p className={styles.altCurrencyLabel}>
							{currency === BTC
								? '$' + (usdAmount || '0.00')
								: (bitcoinAmount || '0.00000000') + ' ' + BTC}
						</p>
						<ChangeArrowsIcon className={styles.altCurrencyIcon} />
					</div>
				)}
				{floor && (
					<div
						className="text-xs text-[#32D583] hover:underline font-semibold cursor-pointer seelect-none"
						onClick={handleSetFloor}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(handleSetFloor)}
					>
						Floor it ::{' '}
						{currency === BTC
							? `${sats} ${per}`
							: `$${formatUsdPer((parseFloat(floor) / 1e8) * exchangeRate).usd}`}
					</div>
				)}
			</div>
		</div>
	)
}

export default ExchangeRateInput
